import type { MessageDescriptor } from 'react-intl';
import type { ArtistProfileKind } from 'fym-shared/src/music24/artistProfile';
import type { ProfileSlot } from './profileSlots';
import { profileMessages as m } from './profileMessages';

/** The colour of a chip (design artist-profile `ap-chip`). */
export type ProfileChipTone = 'ok' | 'amber' | 'nil' | 'bad';

export type ProfileChipKey =
  | 'notMade'
  | 'making'
  | 'failed'
  | 'ready'
  | 'preview'
  | 'onSpotify'
  | 'sending'
  | 'changedOnSpotify'
  | 'uploadFailed'
  | 'setOnYoutube'
  | 'setOnFacebook';

export interface ProfileChip {
  key: ProfileChipKey;
  tone: ProfileChipTone;
}

const SET_KEYS: Partial<Record<ArtistProfileKind, ProfileChipKey>> = {
  youtubeBanner: 'setOnYoutube',
  facebookCover: 'setOnFacebook',
};

const TONES: Readonly<Record<ProfileChipKey, ProfileChipTone>> = {
  notMade: 'nil',
  making: 'amber',
  failed: 'bad',
  ready: 'ok',
  preview: 'ok',
  onSpotify: 'ok',
  sending: 'amber',
  changedOnSpotify: 'amber',
  uploadFailed: 'bad',
  setOnYoutube: 'ok',
  setOnFacebook: 'ok',
};

function chip(key: ProfileChipKey): ProfileChip {
  return { key, tone: TONES[key] };
}

/**
 * The chip of one profile image (design a1–a3, c2): the work first (Making, Failed), then the
 * Spotify upload of the current file, then the manual mark of a banner, then Preview on Free, then
 * Ready. Pure.
 */
export function profileSlotChip(slot: ProfileSlot): ProfileChip {
  if (slot.state === 'making') {
    return chip('making');
  }
  if (slot.state === 'failed') {
    return chip('failed');
  }
  const { current } = slot;
  if (current === null) {
    return chip('notMade');
  }
  switch (current.spotify?.state) {
    case 'sending':
      return chip('sending');
    case 'onSpotify':
      return chip('onSpotify');
    case 'changedOnSpotify':
      return chip('changedOnSpotify');
    case 'failed':
      return chip('uploadFailed');
    case undefined:
      break;
  }
  const setKey = SET_KEYS[slot.kind];
  if (setKey !== undefined && current.setAt !== null) {
    return chip(setKey);
  }
  return chip(current.preview ? 'preview' : 'ready');
}

/** The order in which the Spotify card picks the chip of its two images: the first that one of them has. */
const SPOTIFY_ORDER: readonly ProfileChipKey[] = [
  'making',
  'failed',
  'sending',
  'uploadFailed',
  'changedOnSpotify',
  'onSpotify',
  'preview',
  'ready',
  'notMade',
];

/** The one chip of the Spotify card (design a1): the header and the photo together. Pure. */
export function spotifyCardChip(header: ProfileSlot, avatar: ProfileSlot): ProfileChip {
  const keys = [profileSlotChip(header).key, profileSlotChip(avatar).key];
  const key = SPOTIFY_ORDER.find((candidate) => keys.includes(candidate)) ?? 'notMade';
  return chip(key);
}

/** The press card chip: the three crops together, with the order of the Spotify card. Pure. */
export function slotsChip(slots: readonly ProfileSlot[]): ProfileChip {
  const keys = slots.map((slot) => profileSlotChip(slot).key);
  const key = SPOTIFY_ORDER.find((candidate) => keys.includes(candidate)) ?? 'notMade';
  return chip(key);
}

export const PROFILE_CHIP_TEXT: Readonly<Record<ProfileChipKey, MessageDescriptor>> = {
  notMade: m.chipNotMade,
  making: m.chipMaking,
  failed: m.chipFailed,
  ready: m.chipReady,
  preview: m.chipPreview,
  onSpotify: m.chipOnSpotify,
  sending: m.chipSending,
  changedOnSpotify: m.chipChangedOnSpotify,
  uploadFailed: m.chipUploadFailed,
  setOnYoutube: m.chipSetOnYoutube,
  setOnFacebook: m.chipSetOnFacebook,
};
